import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useJobStore } from '../../stores/jobStore';
import { useTranslation } from 'react-i18next';
import { InputGroup } from '../common/InputGroup';
import { DropdownTypeahead } from '../common/DropdownTypeahead';
import { City } from '../../types/profile';
import { useCitySelector } from '../../hooks/useCitySelector';
import FormLanguageSelector from '../common/FormLanguageSelector';
import { useServiceCategorySelector } from '../../hooks/useServiceCategorySelector';
import { DropdownMultiTypeahead } from '../common/DropdownMultiTypeahead';
import { ToggleButton } from '../common/ToggleButton';
import { Button } from '../common/Button';
import { JobUpsertParams } from '../../types/job';
import { RadioButton } from '../common/RadioButton';
import { ServiceCategory } from '../../types/service';
import { BudgetRange } from '../../types/quote';
import { useProfileStore } from '../../stores/profileStore';
import { toast } from 'react-toastify';
import { createServiceGroupBy } from '../../utils/serviceUtils';
import { useShadowRecruiterStore } from '../../stores/shadowRecruiterStore';

interface UpsertJobFormProps {
  submitButtonLabel?: string;
  submittingButtonLabel?: string;
  secondaryButtonLabel?: string;
  cancelChangesText?: string;
  serviceCategoriesOverride?: ServiceCategory[];
  freelancerServiceCategories?: ServiceCategory[];
  error?: string | null;
  jobParams?: JobUpsertParams | null;
  onCreateJob: (jobParams: JobUpsertParams) => Promise<void>;
  onSecondaryButtonClick?: () => void;
  onCancelChange?: () => void;
}

const UpsertJobForm: React.FC<UpsertJobFormProps> = ({
  submitButtonLabel,
  submittingButtonLabel,
  secondaryButtonLabel,
  cancelChangesText,
  serviceCategoriesOverride,
  error,
  jobParams,
  onCreateJob,
  onSecondaryButtonClick,
  onCancelChange,
  freelancerServiceCategories,
}) => {
  const { t } = useTranslation(['jobs', 'common', 'settings']);
  const { budgetRanges, fetchBudgetRanges } = useJobStore();
  const { profile, suggestNewService } = useProfileStore();
  const {
    shadowRecruiters,
    fetchShadowRecruiters,
    loading: loadingShadowRecruiters,
  } = useShadowRecruiterStore();
  const isAdministrator = profile?.role === 'administrator';

  // Form state
  const [title, setTitle] = useState(jobParams?.title || '');
  const [onSite, setOnSite] = useState(jobParams?.onSite || false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [languages, setLanguages] = useState<string[]>(jobParams?.languages || ['fr']);
  const [startDate, setStartDate] = useState(jobParams?.startDate || '');
  const [endDate, setEndDate] = useState(jobParams?.endDate || '');
  const [description, setDescription] = useState(jobParams?.description || '');
  const [shadowRecruiterId, setShadowRecruiterId] = useState(jobParams?.shadowRecruiterId || '');
  const [budgetRange, setBudgetRange] = useState<BudgetRange | null>(
    jobParams?.budgetRange || null
  );
  const [internalError, setInternalError] = useState('');
  const errorMessageRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState({
    title: '',
    city: '',
    description: '',
    languages: '',
    services: '',
    onsite: '',
    startDate: '',
    endDate: '',
    budgetRange: '',
  });

  useEffect(() => {
    if (budgetRanges.length === 0) {
      fetchBudgetRanges();
    }
  }, [budgetRanges, fetchBudgetRanges]);

  useEffect(() => {
    if (isAdministrator) {
      void fetchShadowRecruiters();
    }
  }, [fetchShadowRecruiters, isAdministrator]);

  useEffect(() => {
    if (errorMessageRef.current && error) {
      errorMessageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [error]);

  const {
    selectedOptions: selectedCities,
    setSelectedOptions: setSelectedCities,
    options: cityOptions,
  } = useCitySelector(jobParams?.city ? [jobParams.city] : profile?.city ? [profile.city] : []);

  const {
    selectedOptions: selectedServiceCategories,
    setSelectedOptions: setSelectedServiceCategories,
    filteredOptions: filteredServiceCategories,
    searchInput: serviceSearchInput,
    setSearchInput: setServiceSearchInput,
  } = useServiceCategorySelector(jobParams?.requiredServices || []);

  const serviceCategoryOptions = serviceSearchInput.trim()
    ? filteredServiceCategories
    : serviceCategoriesOverride;

  const selectedCity = selectedCities[0];

  const setSelectedCity = (city: City | null) => {
    setSelectedCities(city ? [city] : []);
  };

  // Real-time form validation for visual feedback (button stays enabled to allow validation)
  const isFormValid = useMemo(() => {
    // Required fields validation
    if (!title.trim()) return false;
    if (onSite && !selectedCity) return false;
    if (!description.trim()) return false;
    if (!startDate) return false;

    // Start date must be in the future
    if (startDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const start = new Date(startDate);
      if (start < today) return false;
    }

    if (!budgetRange) return false;
    if (selectedServiceCategories.length === 0) return false;

    // Date validation: end date must be after start date
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end < start) return false;
    }

    return true;
  }, [
    title,
    onSite,
    selectedCity,
    description,
    startDate,
    endDate,
    budgetRange,
    selectedServiceCategories,
  ]);

  const validateForm = (): boolean => {
    const newErrors = {
      title: '',
      city: '',
      description: '',
      languages: '',
      services: '',
      onsite: '',
      startDate: '',
      endDate: '',
      budgetRange: '',
    };

    // Required fields validation
    if (!title.trim()) {
      newErrors.title = t('error.required', { ns: 'common' });
    }
    if (onSite && !selectedCity) {
      newErrors.city = t('error.required', { ns: 'common' });
    }
    if (!description.trim()) {
      newErrors.description = t('error.required', { ns: 'common' });
    }
    if (!startDate) {
      newErrors.startDate = t('error.required', { ns: 'common' });
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const start = new Date(startDate);
      if (start < today) {
        newErrors.startDate = t('form.error_date_must_be_future', { ns: 'jobs' });
      }
    }
    if (!budgetRange) {
      newErrors.budgetRange = t('error.required', { ns: 'common' });
    }
    if (selectedServiceCategories.length === 0) {
      newErrors.services = t('error.required', { ns: 'common' });
    }

    // Date validation
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end < start) {
        newErrors.endDate = t('form.error_end_date_before_start', { ns: 'jobs' });
      }
    }

    setErrors(newErrors);
    return Object.values(newErrors).every(error => error === '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInternalError('');

    if (!validateForm()) {
      setInternalError(t('form.error_required', { ns: 'jobs' }));
      return;
    }

    setIsSubmitting(true);

    try {
      await onCreateJob({
        title,
        description,
        onSite,
        languages,
        startDate,
        endDate: endDate || null,
        budgetRange: budgetRange!,

        requiredServices: selectedServiceCategories,
        city: selectedCity!,
        shadowRecruiterId: shadowRecruiterId || null,
      });
    } catch (err) {
      console.error(err);
      setInternalError(t('error.generic', { ns: 'common' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleServiceAdded = (service: ServiceCategory) => {
    setSelectedServiceCategories([...selectedServiceCategories, service]);
  };

  const primaryButtonLabel = isSubmitting
    ? submittingButtonLabel || t('form.submitting')
    : submitButtonLabel || t('form.submit');

  const errorMessage = error || internalError;

  const handleCreateNewService = async (query: string) => {
    try {
      const newService = await suggestNewService({
        label: query,
        description: '',
      });

      handleServiceAdded(newService);

      toast.success(t('serviceAddedSuccess', { ns: 'settings', defaultValue: 'Service added!' }));
    } catch (err) {
      console.error('Error creating service:', err);
      toast.error(t('serviceAddError', { ns: 'settings', defaultValue: 'Could not add service.' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="gap-4 flex flex-col md:grid md:grid-cols-2 md:gap-8">
      {isAdministrator && (
        <div className="md:col-span-2">
          <InputGroup
            label={t('shadow_recruiter.label', {
              ns: 'jobs',
              defaultValue: 'Create on behalf of',
            })}
          >
            <select
              value={shadowRecruiterId}
              onChange={event => setShadowRecruiterId(event.target.value)}
              className="mt-2 block w-full rounded-md border border-gray-300 bg-white px-3 py-3 text-sm text-secondary focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">
                {loadingShadowRecruiters
                  ? t('common:loading', { ns: 'common', defaultValue: 'Loading...' })
                  : t('shadow_recruiter.none', {
                      ns: 'jobs',
                      defaultValue: 'No shadow recruiter',
                    })}
              </option>
              {shadowRecruiters.map(shadowRecruiter => (
                <option key={shadowRecruiter.id} value={shadowRecruiter.id}>
                  {shadowRecruiter.company_name} ({shadowRecruiter.email})
                </option>
              ))}
            </select>
          </InputGroup>
          <span className="mt-1 block text-xs text-gray-500">
            {t('shadow_recruiter.hint', {
              ns: 'jobs',
              defaultValue:
                'Optional. Select a shadow recruiter to keep this job branded as Pigeons until the invite is claimed.',
            })}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <InputGroup
          label={t('form.project_name')}
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder={t('form.project_name_placeholder')}
          required
          error={!!errors.title}
          errorMessage={errors.title}
        />
        <span className="text-xs text-gray-500">{t('form.project_name_help')}</span>
      </div>

      <div className="flex flex-col gap-1">
        <InputGroup
          label={t('form.service_type')}
          required
          error={!!errors.services}
          errorMessage={errors.services}
        >
          <DropdownMultiTypeahead
            selected={selectedServiceCategories}
            onChange={items => {
              setSelectedServiceCategories(items);
              if (errors.services) setErrors(prev => ({ ...prev, services: '' }));
            }}
            options={
              !freelancerServiceCategories ? serviceCategoryOptions : freelancerServiceCategories
            }
            onSearchChange={!freelancerServiceCategories ? setServiceSearchInput : undefined}
            getOptionLabel={item => item.label}
            getOptionValue={item => item.id}
            placeholder={t('form_fields.servicesHint', { ns: 'common' })}
            className="mt-1"
            error={!!errors.services}
            onCreate={!freelancerServiceCategories ? handleCreateNewService : undefined}
            groupBy={createServiceGroupBy(t)}
            inputMode={!freelancerServiceCategories ? 'search' : 'select'}
          />
        </InputGroup>

        <span className="text-xs text-gray-500">{t('form.service_type_help')}</span>
      </div>

      <div className="flex flex-col gap-1">
        <InputGroup label={t('form.location')} error={!!errors.city} errorMessage={errors.city}>
          <DropdownTypeahead
            className="mt-2"
            value={selectedCity?.name || ''}
            onChange={(_val: string, item?: City) => {
              setSelectedCity(item ?? null);
            }}
            options={cityOptions}
            getOptionLabel={item => item.name}
            getOptionValue={item => item.id}
            placeholder={t('form.location_placeholder')}
            showDropdownIcon={false}
            error={!!errors.city}
          />
        </InputGroup>
        <span className="text-xs text-gray-500 mb-1">{t('form.location_help')}</span>
      </div>

      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <InputGroup
          label={t('form.onsite')}
          name="onsite"
          error={!!errors.onsite}
          errorMessage={errors.onsite}
        >
          <ToggleButton
            checked={onSite}
            onChange={e => setOnSite(e.target.checked)}
            label={onSite ? t('common:form_fields.yes') : t('common:form_fields.no')}
            className="mt-2"
          />
        </InputGroup>

        <FormLanguageSelector
          value={languages}
          onChange={setLanguages}
          error={errors.languages}
          required
        />
      </div>

      <InputGroup
        label={t('form.price_range')}
        required
        error={!!errors.budgetRange}
        errorMessage={errors.budgetRange}
      >
        <div className="grid grid-cols-2 gap-4 mt-2 md:grip-cols-3">
          {budgetRanges.map(option => (
            <RadioButton
              name="budgetRange"
              key={option.id}
              label={t(`budget_ranges.${option.code}`, { ns: 'common' })}
              value={option.id}
              checked={budgetRange?.id === option.id}
              onChange={() => setBudgetRange(option)}
            />
          ))}
        </div>
      </InputGroup>

      <div className="flex flex-col md:flex-row gap-4">
        <InputGroup
          required
          type="date"
          label={t('form.start_date')}
          name="start_date"
          error={!!errors.startDate}
          errorMessage={errors.startDate}
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
        />

        <InputGroup
          type="date"
          label={t('form.end_date')}
          name="end_date"
          error={!!errors.endDate}
          errorMessage={errors.endDate}
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
        />
      </div>
      <div className="col-span-2">
        <InputGroup
          label={t('form.description')}
          required
          error={!!errors.description}
          errorMessage={errors.description}
          as="textarea"
          placeholder={t('form.description_placeholder')}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
      </div>

      {/* Error and submit */}
      {errorMessage && (
        <div ref={errorMessageRef} className="col-span-2 text-red-600 font-semibold text-center">
          {errorMessage}
        </div>
      )}

      <div className="col-span-2 flex flex-col gap-4 mt-8 md:gap-8 md:mt-4 md:flex-row md:flex-row-reverse md:items-center">
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full md:w-auto md:px-8"
          title={
            !isFormValid
              ? t('form.complete_required_fields', {
                  ns: 'jobs',
                  defaultValue: 'Please complete all required fields',
                })
              : undefined
          }
        >
          {primaryButtonLabel}
        </Button>

        {cancelChangesText && onCancelChange && (
          <Button
            className="w-full md:w-auto md:px-8"
            variant="simple-text"
            type="button"
            onClick={() => {
              onCancelChange();
            }}
          >
            {cancelChangesText}
          </Button>
        )}

        {secondaryButtonLabel && onSecondaryButtonClick && (
          <Button
            type="button"
            onClick={() => {
              onSecondaryButtonClick();
            }}
            className="w-full md:w-auto md:px-8"
            variant="transparent-with-border"
          >
            {secondaryButtonLabel}
          </Button>
        )}
      </div>
    </form>
  );
};

export default UpsertJobForm;
